import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { NodeState, NewNode, EditNode, TestNodeOsAccount, TestNodeDomainAccount } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';
import router from '../../../router/index';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<NodeState, RootState> = {
    /**
     * ノード一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignoreGroupId 除外ノードグループID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { word = '', ignoreGroupId = undefined, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: word,
            ignore_group_id: ignoreGroupId,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Node.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノード新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async new({ commit }) {
        commit(types.NEW_NODE);
    },

    /**
     * ノード新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewNode} newNode 新規情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newNode: NewNode) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.Node.CREATE, params: newNode },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.CREATED);
                dispatch('toastMessage', `${newNode.name} ${message}`, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ノードの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ノードID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Node.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノード更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditNode} editNode 更新するノード情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editNode: EditNode) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.Node.UPDATE.replace(':id', editNode.node.id.toString()),
                    params: editNode,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ノード情報・OSアカウント情報で接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {TestNodeOsAccount} testNodeOsAccount 接続先ノード情報
     * @returns {Promise<void>}
     */
    async osConnection({ dispatch, commit, rootState }, testNodeOsAccount: TestNodeOsAccount) {
        try {
            commit(types.OS_CONNECTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.OS_CONNECTION,
                    params: testNodeOsAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                //システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.OS_CONNECTION_SUCCESS);

                //成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.CONNECTED);
                dispatch('toastMessage', `${testNodeOsAccount.account} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケージョンエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.OS_CONNECTION_FAILURE, params);
        }
    },

    /**
     * ノード情報・OSアカウント情報で接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} id ノードID
     * @param {any} testNodeOsAccount 接続先ノード情報
     */
    async osCreatedConnection({ dispatch, commit, rootState }, { id, testNodeOsAccount }) {
        try {
            commit(types.OS_CREATED_CONNECTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.OS_CONNECTION_AFTER_CREATED.replace(':id', id.toString()),
                    params: testNodeOsAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                //システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.OS_CREATED_CONNECTION_SUCCESS);

                //成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.CONNECTED);
                dispatch('toastMessage', `${testNodeOsAccount.account} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケージョンエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.OS_CREATED_CONNECTION_FAILURE, params);
        }
    },

    /**
     * ノード情報・ドメインアカウント情報で接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} testNodeDomainAccount 接続先ノード情報
     * @param {any} domainName ドメイン名
     * @returns {Promise<void>}
     */
    async domainConnection({ dispatch, commit, rootState }, { testNodeDomainAccount, domainName }) {
        try {
            commit(types.DOMAIN_CONNECTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.DOMAIN_CONNECTION,
                    params: testNodeDomainAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                //システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DOMAIN_CONNECTION_SUCCESS);

                //成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.CONNECTED);
                dispatch('toastMessage', `${domainName} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケージョンエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DOMAIN_CONNECTION_FAILURE, params);
        }
    },

    /**
     * ノード削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ノードID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.Node.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignoreGroupId 除外ノードグループID
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { word = '', ignoreGroupId = undefined, sort } = {}) {
        const payload = {
            word: word,
            ignore_group_id: ignoreGroupId,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.Node.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS, resData);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 自動ログインアプリ定義追加前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearApps({ commit }) {
        commit(types.CLEAR_APPS);
    },

    /**
     * 自動ログインアプリ定義追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {any} auto_login_definition_ids 追加する自動ログインアプリ定義
     * @returns {Promise<void>}
     */
    async addApps({ dispatch, commit, rootState }, { node_id, auto_login_definition_ids }) {
        try {
            commit(types.ADD_APPS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.ADD_APPS.replace(':id', node_id.toString()),
                    params: { auto_login_definition_ids },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADD_APPS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.ADD_APPS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ADD_APPS_FAILURE, params);
        }
    },

    /**
     * 自動ログインアプリ定義除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {any} auto_login_definition_ids 除外する自動ログインアプリ定義情報
     * @returns {Promise<void>}
     */
    async removeApps({ dispatch, commit, rootState }, { node_id, auto_login_definition_ids }) {
        try {
            commit(types.REMOVE_APPS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.REMOVE_APPS.replace(':id', node_id.toString()),
                    params: { auto_login_definition_ids },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVE_APPS_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.REMOVE_APPS);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVE_APPS_FAILURE, params);
        }
    },

    /**
     * ログイン履歴収集除外条件追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {any} node_login_history_exclude_condition_ids ログイン履歴収集除外条件
     * @returns {Promise<void>}
     */
    async addExcludes({ dispatch, commit, rootState }, { node_id, node_login_history_exclude_condition_ids }) {
        try {
            commit(types.ADD_EXCLUDES_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.ADD_EXCLUDES.replace(':id', node_id.toString()),
                    params: { node_login_history_exclude_condition_ids },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADD_EXCLUDES_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.ADD_EXCLUDES);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ADD_EXCLUDES_FAILURE, params);
        }
    },

    /**
     * ログイン履歴収集除外条件除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {any} node_login_history_exclude_condition_ids 除外する自動ログインアプリ定義
     * @returns {Promise<void>}
     */
    async removeExcludes({ dispatch, commit, rootState }, { node_id, node_login_history_exclude_condition_ids }) {
        try {
            commit(types.REMOVE_EXCLUDES_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.REMOVE_EXCLUDES.replace(':id', node_id.toString()),
                    params: { node_login_history_exclude_condition_ids },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVE_EXCLUDES_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.REMOVE_EXCLUDES);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVE_EXCLUDES_FAILURE, params);
        }
    },

    /**
     * SAMLサービスプロバイダノード一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async samlNodesSearch({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: searchItems.word,
            node_group_id: searchItems.node_group_id,
            ignore_node_ids: searchItems.ignore_node_ids,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SAML_NODES_SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Node.SAML_NODES_SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SAML_NODES_SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SAML_NODES_SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * SAML連携用の証明書ダウンロード処理開始
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async samlCertificateDownloadStart({ commit }) {
        commit(types.SAML_CERTIFICATE_DOWNLOAD_START);
    },

    /**
     * SAML連携用の証明書ダウンロード処理正常終了
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async samlCertificateDownloadSuccess({ commit }) {
        commit(types.SAML_CERTIFICATE_DOWNLOAD_SUCCESS);
    },

    /**
     * SAML連携用の証明書ダウンロード処理エラー終了
     * @param {any} commit Vuex変数
     * @param resData エラーレスポンス
     * @returns {Promise<void>}
     */
    async samlCertificateDownloadFailure({ commit }, resData) {
        const params = {
            errorTitle: resData.title,
            errorDetails: resData.errors,
        };
        commit(types.SAML_CERTIFICATE_DOWNLOAD_FAILURE, params);
    },

    /**
     * SAML連携用の証明書再発行
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ノードID
     * @param {number} certificate_expiration_at 証明書の有効期限(YYYY-MM-DD)
     * @returns {Promise<void>}
     */
    async samlCertificateReissue({ dispatch, commit, rootState }, { id, certificate_expiration_at }) {
        try {
            commit(types.SAML_CERTIFICATE_REISSUE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.Node.SAML_CERTIFICATE_REISSUE.replace(':id', id.toString()),
                    params: { certificate_expiration_at },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SAML_CERTIFICATE_REISSUE_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.SAML_CERTIFICATE_REISSUE);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SAML_CERTIFICATE_REISSUE_FAILURE, params);
        }
    },

    /**
     * SAML連携用のメタデータダウンロード処理開始
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async samlMetadataDownloadStart({ commit }) {
        commit(types.SAML_METADATA_DOWNLOAD_START);
    },

    /**
     * SAML連携用のメタデータダウンロード処理正常終了
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async samlMetadataDownloadSuccess({ commit }) {
        commit(types.SAML_METADATA_DOWNLOAD_SUCCESS);
    },

    /**
     * SAML連携用のメタデータダウンロード処理エラー終了
     * @param {any} commit Vuex変数
     * @param resData エラーレスポンス
     * @returns {Promise<void>}
     */
    async samlMetadataDownloadFailure({ commit }, resData) {
        const params = {
            errorTitle: resData.title,
            errorDetails: resData.errors,
        };
        commit(types.SAML_METADATA_DOWNLOAD_FAILURE, params);
    },

    /**
     * SAML連携用の証明書再発行後、ノード詳細取得をし、発行済み証明書の有効期限のみ更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ノードID
     * @returns {Promise<void>}
     */
    async showNewSamlCertExpAt({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_NEW_SAML_CERT_EXP_AT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Node.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_NEW_SAML_CERT_EXP_AT_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_NEW_SAML_CERT_EXP_AT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードのログイン履歴の収集間隔の選択肢を取得する
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async loginHistoryCollectionIntervalTypeAcquisition({ dispatch, commit }) {
        try {
            commit(types.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.Node.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.LOGIN_HISTORY_COLLECTION_INTERVAL_TYPE_ACQUISITION_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 自動ログインアプリ定義追加前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearExcludes({ commit }) {
        commit(types.CLEAR_EXCLUDES);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
